// components/PaymentHistory.jsx - Mobile-optimized professional spacing
import { useState } from 'react';
import { DollarSign, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';

export function PaymentHistory({ paymentHistory }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!paymentHistory) {
    return null;
  }

  const { section, title, payments, message, count } = paymentHistory;

  // Empty state
  if (section === 'empty') {
    return (
      <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 pb-3 border-b">
          <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">{title}</h2>
        </div>
        <div className="text-center py-8">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{message}</p>
        </div>
      </div>
    );
  }

  // Group under year headings only when the list spans more than one year
  const paymentYear = (payment) => new Date(payment.date).getFullYear();
  const distinctYears = [...new Set((payments || []).map(paymentYear))];
  const showYearHeadings = distinctYears.length > 1;

  // Build display rows: an optional year heading followed by that year's payments
  const rows = [];
  let lastYear = null;
  (payments || []).forEach((payment, i) => {
    const year = paymentYear(payment);
    if (showYearHeadings && year !== lastYear) {
      rows.push({ type: 'year', year });
      lastYear = year;
    }
    rows.push({ type: 'payment', payment, index: i });
  });

  // Has payments - collapsible
  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      {/* Header - Collapsible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors border-b"
      >
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 truncate">{title}</h2>
          <span className="bg-green-100 text-green-700 text-xs sm:text-sm font-semibold px-2.5 sm:px-3 py-1 rounded-full whitespace-nowrap">
            {count} payment{count !== 1 ? 's' : ''}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 flex-shrink-0 ml-2" />
        ) : (
          <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 flex-shrink-0 ml-2" />
        )}
      </button>

      {/* Payment List */}
      {isExpanded && (
        <div className="divide-y divide-gray-200">
          {rows.map(row => {
            if (row.type === 'year') {
              return (
                <div
                  key={`year-${row.year}`}
                  className="px-4 sm:px-6 py-2 bg-gray-50"
                >
                  <span className="text-xs sm:text-sm font-bold text-gray-500 tracking-wide">
                    {row.year}
                  </span>
                </div>
              );
            }

            const payment = row.payment;
            const isCreditApplication = payment.method === 'Credit Applied';

            return (
              <div
                key={row.index}
                className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition-colors"
              >
                <div className="flex items-center justify-between gap-3 sm:gap-4">
                  {/* Left side - Icon + Text */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                      <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm sm:text-base text-gray-800">
                        Payment Received
                      </div>
                      {isCreditApplication && (
                        <div className="text-xs sm:text-sm text-green-700 font-medium">
                          (from CR Bal)
                        </div>
                      )}
                      <div className="text-xs sm:text-sm text-gray-600 mt-0.5">
                        {new Date(payment.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - Amount */}
                  <div className="text-xl sm:text-2xl font-bold text-green-600 flex-shrink-0">
                    ${parseFloat(payment.amount).toFixed(2)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PaymentHistory;